import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Formik, Form, FormikProps } from "formik"
import SidePanel from "../../side-panel"
import {
  useToast,
  useValidateEmailAndNMLS,
  usePendingSubscriptionsCount
} from "../../../hooks"
import {
  CreatePendingSubscriptionPayload,
  PendingSubscriptionFull,
  UpdatePendingSubscriptionPayload,
  GooglePlace
} from "../../../../types"
import {
  convertAddressObjToPlaceString,
  convertGooglePlaceToAddressObj,
  validateZipcodeForPayload
} from "../../../utils"
import {
  useCreatePendingSubscriptionMutation,
  useUpdatePendingSubscriptionMutation,
  useDeletePendingSubscriptionMutation
} from "../../../../redux/queries"
import { useSearchLoanOfficerQuery } from "../../../../redux/queries"
import ManageEngagementBasic from "../../../../common/ui/engagements/manage/sections/basic"
import ManageEngagementSelectors from "../../../../common/ui/engagements/manage/sections/selectors"
import ManageEngagementMisc from "../../../../common/ui/engagements/manage/sections/misc"
import { useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import { dreamRateFormSchema } from "./dream-rate-form-schema"
import { getPendingSubscriptionStatus } from "../../../utils/dream-rate"

type Props = {
  open: boolean
  data?: PendingSubscriptionFull
  onClose: () => void
}

const convertPendingSubscriptionToInitialValues = (
  data?: PendingSubscriptionFull,
  loid?: string
): CreatePendingSubscriptionPayload => {
  const location = data?.quote_info?.location
  const locationObj = {
    city: location?.city ?? "",
    county: location?.county ?? "",
    state: location?.state ?? "",
    street_address: location?.street_address ?? "",
    zipcode: location?.zipcode ?? ""
  }

  return {
    // Basic fields
    active: "1" as const,
    deleted: "0" as const,
    email: data?.email ?? "",
    first_name: data?.first_name ?? "",
    last_name: data?.last_name ?? "",
    phone: data?.phone ?? "",
    loid: data?.loid ?? loid ?? "",

    // Loan scenario fields
    loan_type: data?.quote_info?.loan_type ?? "conventional",
    loan_term: data?.quote_info?.loan_term?.toString() ?? "30",
    arm_term: data?.quote_info?.arm_term?.toString() ?? "",
    loan_purpose: data?.quote_info?.loan_purpose ?? "purchase",
    loan_amount: data?.quote_info?.loan_amount?.toString() ?? "",
    credit_score: data?.quote_info?.credit_score ?? 700,
    property_type: data?.quote_info?.property_type ?? "single_family_home",
    residency_type: data?.quote_info?.residency_type ?? "primary_home",
    list_price: data?.quote_info?.list_price?.toString() ?? "",
    cash_out: data?.quote_info?.cash_out?.toString() ?? "",
    first_time_homebuyer: false,
    va_funding_fee_type:
      data?.quote_info?.loan_type === "va"
        ? data?.quote_info?.va_funding_fee_type ?? ""
        : "",
    nonqm_program:
      data?.quote_info?.loan_type === "nonqm"
        ? data?.quote_info?.nonqm_program ?? ""
        : "",

    // Location fields
    ...locationObj,
    location: convertAddressObjToPlaceString(locationObj),

    // Admin fields
    taxes: data?.quote_info?.admin_fields?.taxes ?? 0,
    insurance: data?.quote_info?.admin_fields?.insurance ?? 0,
    seller_credit: data?.quote_info?.admin_fields?.seller_credit ?? 0,

    // Extra flags
    waive_escrows: data?.quote_info?.waive_escrows ?? false,

    // Rate drop specific
    trigger_rate: data?.trigger_rate ?? 0,
    client_request_id: data?.client_request_id ?? undefined
  } as CreatePendingSubscriptionPayload
}

const ManageDreamRatePanel: FC<Props> = ({ open, data, onClose }) => {
  const { pushErrorToast, pushSuccessToast } = useToast()
  const formRef = useRef<FormikProps<CreatePendingSubscriptionPayload>>(null)
  const isCreateMode = !data?.id
  const { maxReached } = usePendingSubscriptionsCount()

  const [createPending, createState] = useCreatePendingSubscriptionMutation()
  const [updatePending, updateState] = useUpdatePendingSubscriptionMutation()
  const [deletePending, deleteState] = useDeletePendingSubscriptionMutation()
  const { validateSubscriptionEmail, validateSubscriptionPhone } =
    useValidateEmailAndNMLS()

  const derivedStatus = data ? getPendingSubscriptionStatus(data) : null
  const canDelete =
    !isCreateMode &&
    (derivedStatus === "pending" || derivedStatus === "cancelled")

  const userDetails = useAppSelector(selectUserDetails)
  const selectedBrandId = userDetails?.brand_id

  const [googlePlace, setGooglePlace] = useState<GooglePlace>()
  const [selectedLoanOfficer, setSelectedLoanOfficer] = useState<any>(null)

  const conflictingSubRef = useRef<{ email?: string; phone?: string }>({})

  // Reset mutation states when panel closes to prevent stale toast messages
  useEffect(() => {
    if (!open) {
      createState.reset()
      updateState.reset()
      deleteState.reset()
    }
  }, [open, createState, updateState, deleteState])

  // Fetch loan officer details when selected
  const { data: loanOfficerData } = useSearchLoanOfficerQuery(
    { loid: selectedLoanOfficer?.loid ?? "" },
    { skip: !selectedLoanOfficer?.loid }
  )

  const initialValues = useMemo(
    () => convertPendingSubscriptionToInitialValues(data, userDetails?.id),
    [data, userDetails?.id]
  )

  const isLoading =
    createState.isLoading || updateState.isLoading || deleteState.isLoading

  const setFormField = useCallback((name: string, value: any) => {
    if (formRef.current) {
      formRef.current.setFieldValue(name, value)
    }
  }, [])

  const handleLoanOfficerChange = useCallback(
    (loid: string) => {
      setFormField("loid", loid)
      // We'll need to fetch the loan officer details to get the NMLS
      // For now, we'll store the loid and fetch the details
      setSelectedLoanOfficer({ loid })
    },
    [setFormField]
  )

  // Update form fields when Google Place is selected
  useEffect(() => {
    if (googlePlace) {
      const address = convertGooglePlaceToAddressObj(googlePlace)
      setFormField("city", address.city ?? "")
      setFormField("county", address.county ?? "")
      setFormField("state", address.state ?? "")
      setFormField("street_address", address.street_address ?? "")
      setFormField("zipcode", address.zipcode ?? "")
    }
  }, [googlePlace, setFormField])

  const onSubmit = useCallback(async () => {
    const values = formRef.current?.values
    if (!values) return

    // Minimal front-end validation
    if (!values.email || !values.trigger_rate) {
      pushErrorToast("Email and Trigger rate are required")
      return
    }

    // Zipcode validation - only required when location details are shown
    const zipcodeErrors = validateZipcodeForPayload(values, googlePlace)
    if (zipcodeErrors.zipcode) {
      pushErrorToast(zipcodeErrors.zipcode)
      return
    }

    if (isCreateMode) {
      // Extract NMLS from selected loan officer
      const selectedLO = loanOfficerData?.[0]
      const nmls = selectedLO?.nmls || ""

      // Send the full form values with NMLS - API wrapper will transform to backend format
      const payloadWithNMLS = {
        ...values,
        nmls
      }
      await createPending(payloadWithNMLS)
    } else if (data?.id) {
      const payload: UpdatePendingSubscriptionPayload = {
        id: data.id,
        ...values
      }
      await updatePending(payload)
    }
  }, [
    createPending,
    data?.id,
    isCreateMode,
    pushErrorToast,
    updatePending,
    loanOfficerData,
    googlePlace
  ])

  const onDelete = useCallback(async () => {
    if (!data?.id) return
    await deletePending({ id: data.id })
  }, [data?.id, deletePending])

  useEffect(() => {
    if (deleteState.isSuccess) {
      onClose()
      pushSuccessToast("Deleted dream rate")
      return
    }
    if (deleteState.isError) {
      const msg =
        (deleteState.error as any)?.data?.message ||
        (deleteState.error as any)?.message
      pushErrorToast(msg || "Failed to delete dream rate")
      return
    }
  }, [
    deleteState.isSuccess,
    deleteState.isError,
    deleteState.error,
    onClose,
    pushErrorToast,
    pushSuccessToast
  ])

  useEffect(() => {
    const mutationData = isCreateMode ? createState.data : updateState.data
    const isMutationSuccess = isCreateMode
      ? createState.isSuccess
      : updateState.isSuccess
    const isMutationError = isCreateMode
      ? createState.isError
      : updateState.isError
    const mutationError = isCreateMode ? createState.error : updateState.error
    const responseStatus = (mutationData as any)?.status
    const isExplicitFailureStatus =
      responseStatus === "error" || responseStatus === "failed"
    const apiErrorMessage =
      (mutationData as any)?.message ||
      (mutationError as any)?.data?.message ||
      (mutationError as any)?.message

    // Some endpoints return non-"success" truthy statuses (or omit status)
    // while still succeeding. Treat explicit "error" as failure, otherwise
    // trust RTK Query mutation success state.
    const success = isMutationSuccess && !isExplicitFailureStatus
    const error =
      isMutationError || (isMutationSuccess && isExplicitFailureStatus)

    if (success) {
      onClose()
      pushSuccessToast(
        `Successfully ${isCreateMode ? "created" : "updated"} dream rate`
      )
    } else if (error) {
      pushErrorToast(
        apiErrorMessage ||
          `Failed to ${isCreateMode ? "create" : "update"} dream rate`
      )
    }
  }, [
    createState.error,
    createState.isError,
    createState.isSuccess,
    createState.data,
    isCreateMode,
    onClose,
    pushErrorToast,
    pushSuccessToast,
    updateState.error,
    updateState.isError,
    updateState.isSuccess,
    updateState.data
  ])

  const onConflict = useCallback(
    (field: "email" | "phone", conflicting: { id?: string } | undefined) => {
      conflictingSubRef.current[field] = conflicting?.id
    },
    []
  )

  const makeRenderError = useCallback(
    (field: "email" | "phone") => (msg: string) => {
      const id = conflictingSubRef.current[field]
      if (!id) return msg
      const parts = msg.split("Subscriptions")
      if (parts.length === 1) return msg
      return (
        <>
          {parts[0]}
          <Link
            to={`/engagements/${id}`}
            className="underline"
            onClick={onClose}
          >
            Subscriptions
          </Link>
          {parts[1]}
        </>
      )
    },
    []
  )

  const validate = useCallback(
    async (values: CreatePendingSubscriptionPayload) => {
      const errors: any = {}

      Object.assign(
        errors,
        await validateSubscriptionEmail(
          { email: values.email, nmls: "" } as any,
          selectedBrandId || "",
          undefined,
          "dreamRate",
          onConflict
        )
      )

      if (values.phone) {
        Object.assign(
          errors,
          await validateSubscriptionPhone(
            { phone: values.phone } as any,
            selectedBrandId || "",
            undefined,
            "dreamRate",
            onConflict
          )
        )
      }

      // Zipcode validation - only required when location details are shown
      const zipcodeErrors = validateZipcodeForPayload(values, googlePlace)
      Object.assign(errors, zipcodeErrors)

      return errors
    },
    [
      validateSubscriptionEmail,
      validateSubscriptionPhone,
      googlePlace,
      selectedBrandId,
      onConflict
    ]
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={dreamRateFormSchema}
      onSubmit={onSubmit}
      validateOnBlur
      validateOnChange
      validate={validate}
      enableReinitialize
      innerRef={formRef}
    >
      {({ isValid, values, errors, dirty }) => {
        return (
          <SidePanel
            title={`${isCreateMode ? "Add new" : "Edit"} dream rate`}
            open={open}
            onClose={onClose}
            actions={[
              {
                label: "Delete dream rate",
                type: "danger",
                position: "left",
                onClick: onDelete,
                disabled: isLoading,
                hidden: !canDelete
              },
              {
                label: isCreateMode ? "Add dream rate" : "Update dream rate",
                type: "primary",
                onClick: onSubmit,
                disabled:
                  isLoading ||
                  !isValid ||
                  !dirty ||
                  (isCreateMode && maxReached)
              }
            ]}
          >
            <Form>
              <ManageEngagementBasic
                values={values}
                loSearchParams={{ brand_id: selectedBrandId }}
                onlyAdmins={false}
                setFormField={setFormField}
                setGooglePlace={setGooglePlace}
                onLoanOfficerChange={handleLoanOfficerChange}
                hideStatusField
                googlePlace={googlePlace}
                emailRenderError={makeRenderError("email")}
                phoneRenderError={makeRenderError("phone")}
              />

              <ManageEngagementSelectors
                values={values}
                setFormField={setFormField}
                hasDreamRate
                nonqmEnabled
              />
              <ManageEngagementMisc
                values={values}
                setFormField={setFormField}
                hasWaiveEscrows
              />
            </Form>
          </SidePanel>
        )
      }}
    </Formik>
  )
}

export default ManageDreamRatePanel
